import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CoreService } from '../../../services/core.service';

type Step = 'upload' | 'processing' | 'result';

@Component({
  selector: 'app-leonardo-ai-old',
  templateUrl: './leonardo-ai.component.html',
  standalone: true,
  imports: [CommonModule],
  styleUrls: ['./leonardo-ai.component.scss'],
})
export class LeonardoAiComponentOld {

  step: Step = 'upload';
  selectedFile: File | null = null;
  previewUrl: string | null = null;
  videoUrl: string | null = null;
  errorMessage: string | null = null;
  isDragging = false;
  progressValue = 0;
  processingLabel = 'Uploading image...';

  constructor(private coreService: CoreService) {}

  // ── File selection ──────────────────────────────────────────────────────────

  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) this.setFile(file);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(): void {
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    const file = event.dataTransfer?.files[0];
    if (file) this.setFile(file);
  }

  private setFile(file: File): void {
    this.selectedFile = file;
    this.errorMessage = null;
    const reader = new FileReader();
    reader.onload = (e) => (this.previewUrl = e.target?.result as string);
    reader.readAsDataURL(file);
  }

  // ── Upload → Process pipeline ───────────────────────────────────────────────

  upload(): void {
    if (!this.selectedFile) return;

    this.step = 'processing';
    this.progressValue = 10;
    this.processingLabel = 'Uploading image...';

    const formData = new FormData();
    formData.append('image', this.selectedFile);

    // coreService detects FormData and skips Content-Type header automatically
    this.coreService.postData('aiVideo/upload', formData).subscribe({
      next: (res) => {
        this.progressValue = 40;
        this.processingLabel = 'Analysing image & generating video...';
        this.process(res.tempPath);
      },
      error: (err) => {
        console.log('upload error', err);
        this.handleError(err);
      }
    });
  }

  private process(tempPath: string): void {
    const ticker = setInterval(() => {
      if (this.progressValue < 90) this.progressValue += 0.5;
    }, 1000);

    this.coreService.postData('aiVideo/process', { tempPath }).subscribe({
      next: (res) => {
        clearInterval(ticker);
        this.progressValue = 100;
        this.processingLabel = 'Done!';
        setTimeout(() => {
          this.videoUrl = res.videoUrl;
          this.step = 'result';
        }, 400);
      },
      error: (err) => {
        clearInterval(ticker);
        this.handleError(err);
      },
    });
  }

  private handleError(err: any): void {
    this.errorMessage = err?.error?.error || 'Something went wrong. Please try again.';
    this.step = 'result';
  }

  // ── Reset ───────────────────────────────────────────────────────────────────

  reset(): void {
    this.step = 'upload';
    this.selectedFile = null;
    this.previewUrl = null;
    this.videoUrl = null;
    this.errorMessage = null;
    this.progressValue = 0;
    this.processingLabel = 'Uploading image...';
  }
}
